import React, { useCallback } from 'react';
import { useDropzone } from 'react-dropzone';
import { useAuth0 } from '@auth0/auth0-react';
import { Button, message, Tooltip } from 'antd';
import { PaperClipOutlined, CloudUploadOutlined } from '@ant-design/icons';
import { uploadFile } from '../services/api';

interface FileUploadProps {
  onFileUpload: (fileId: string) => void;
}

const FileUpload: React.FC<FileUploadProps> = ({ onFileUpload }) => {
  const { getAccessTokenSilently } = useAuth0();

  const onDrop = useCallback(async (acceptedFiles: File[]) => {
    for (const file of acceptedFiles) {
      // Check file size (1MB limit)
      const maxSize = 1024 * 1024; // 1MB
      if (file.size > maxSize) {
        message.error(`File ${file.name} exceeds the 1MB size limit.`);
        continue;
      }

      const hideLoading = message.loading(`Uploading ${file.name}...`, 0);

      try {
        const token = await getAccessTokenSilently();
        const response = await uploadFile(file, token);
        onFileUpload(response.fileId);
        hideLoading();
        message.success(`${file.name} uploaded successfully!`);
      } catch (error) {
        hideLoading();
        console.error('Error uploading file:', error);
        message.error(`Failed to upload ${file.name}. Please try again.`);
      }
    }
  }, [getAccessTokenSilently, onFileUpload]);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    multiple: true,
    maxSize: 1024 * 1024, // 1MB
    accept: {
      'image/*': ['.jpeg', '.jpg', '.png', '.gif'],
      'application/pdf': ['.pdf'],
      'text/plain': ['.txt'],
      'application/msword': ['.doc'],
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document': ['.docx'],
    },
  });

  return (
    <div {...getRootProps()} className="file-upload-container">
      <input {...getInputProps()} />
      <Tooltip title="Upload files (max 1MB each)" placement="top">
        <Button
          icon={isDragActive ? <CloudUploadOutlined /> : <PaperClipOutlined />}
          className={`file-upload-button ${isDragActive ? 'drag-active' : ''}`}
          size="large"
        />
      </Tooltip>
    </div>
  );
};

export default FileUpload;